import type {PilotFeedbackInput, PilotFeedbackReport, FeedbackStatus} from '../shared/pilot-feedback.js';
import type { MaterialsClient, MaterialsDraftView, MaterialsInput, MaterialsReviewMode } from './materials.js';
import type { FlipCardContent } from '../shared/class-materials.js';
import type { LibraryFeedbackBinding, LibraryRating, LibraryReleaseMetrics } from '../shared/library-feedback.js';
export type { LibraryFeedbackBinding } from '../shared/library-feedback.js';
import type { LibraryShareInput } from '../shared/library.js';
import type { LibraryCatalogResult, LibraryDetailResult, LibraryQuery, RatedLibraryRelease } from './library-types.js';
import { libraryQueryString } from './library-types.js';
import type { ResourceView } from '../server/resources/service.js';
export type { ResourceView } from '../server/resources/service.js';
/**
 * TIP Shell — backend (monolith) client. The Shell is the trusted parent; it is the
 * ONLY thing that talks to the server on the frame's behalf. All calls carry the SSO
 * session cookie (credentials:'include'); the server resolves tenant + user from it.
 *
 * Endpoints (see report for the full contract the SERVER builder must implement):
 *   POST /gate/mount        → GATE: computes + returns the EffectiveGrant (tier) at launch
 *   POST /identity/resolve  → REGISTRAR (keyring): tag → first name (Shell-side only)
 *   POST /broker            → BROKER: Door-1 getMyToken / writeScore / getScores
 *   POST /admin/kill        → GOVERNANCE: quarantine an app (tripwire enforcement).
 *                             With source='egress_tripwire' this ALSO writes the
 *                             hash-chained egress_tripwire audit event atomically —
 *                             no separate best-effort ledger call to drop.
 */
import type { BrokerMethod } from './protocol.js';
import type { EffectiveGrantSnapshot, Tier } from '../shared/index.js';

/** GATE mount result. `deny` true ⇒ fail-closed, no frame is mounted. */
export type StudentLibraryRating = LibraryRating;
export interface StudentLibraryFeedback { eligible: boolean; releaseId?: string; rating?: StudentLibraryRating; }

export interface GateMountResult {
  /** Trusted shell only. Never include this receipt in frame init or a bridge response. */
  library_feedback?: LibraryFeedbackBinding;
  app_name?: string;
  deny: boolean;
  reason?: string;
  grant?: EffectiveGrantSnapshot; // carries the COMPUTED tier — authoritative
  self?: { tag: string }; // name-free (Gate/roster hold tags, not names)
  roster?: { tag: string }[];
  context?: import('../shared/sdk.js').TipActivityContext; // Shell-injected activity scope (opaque, non-PII)
  /** The bound class's school look (migration 0016) → `init.theme`. Presentation only. */
  theme?: { schoolName: string; mascot: string; primary: string; accent: string };
  /** CANONICAL app id (apps.id UUID) for an authorized mount — the frame URL must use
   *  THIS, not whatever slug the caller mounted by. */
  app_id?: string;
  /** Signed CSP-report attribution token — threaded into the frame URL (`?rpt=`) verbatim. */
  csp_report_token?: string;
  /** Signed app-source token — threaded into the frame URL (`?src=`) verbatim.
   *  Present for a generated app; the sandbox uses it to fetch the app's source. */
  app_source_token?: string;
}

/** SSO session as resolved from the session cookie (server derives tenant + user). */
export interface SessionInfo {
  userId: string;
  tenantId: string;
  role: string;
}

export interface ResolveResult {
  resolved: { tag: string; given?: string }[];
}

/** One broker-authoritative leaderboard row. `given` (first name) is present only when
 *  the grant allows names; it is painted in the PARENT DOM and never sent into the frame. */
export interface LeaderboardRow {
  tag: string;
  given?: string;
  score: number;
  max_score: number;
}

/** GATE leaderboard result. `deny` true ⇒ nothing is painted (fail-closed). */
export interface LeaderboardResult {
  deny: boolean;
  reason?: string;
  scope?: { classId: string; assignmentId: string };
  rows?: LeaderboardRow[];
}

export interface BrokerResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

// ── Teacher-console shapes (mirror server/release/console.ts + release/index.ts) ──

export interface AssignmentOverview {
  assignmentId: string;
  title: string;
  appName: string;
  maxScore: number;
  realScores: number;
  posted: number;
  failed: number;
  pending: number;
}
export interface ClassOverview {
  classId: string;
  name: string;
  studentCount: number;
  /** The ACTIVE-provider link (release destination); null when unlinked or no connection. */
  link: { provider: string; externalClassId: string } | null;
  /** Links outside the active provider (all links during a credential outage) — display-only. */
  otherLinks: { provider: string; externalClassId: string }[];
  assignments: AssignmentOverview[];
  /** The class's school (migration 0016) — presentation only; absent on older servers. */
  school?: SchoolLook | null;
}

/** A school as the console shows it: how its apps look, never who is in it. */
export interface SchoolLook { name: string; mascot: string; primary: string; accent: string }
export interface School extends SchoolLook { id: string }
export interface SchoolsView {
  schools: School[];
  classes: { classId: string; name: string; schoolId: string | null }[];
}
export type SchoolSaveResult = { ok: true; school: School } | { ok: false; reason: string };
export type OverviewResult =
  | { deny: true; reason: string }
  | { deny: false; providerConfigured: boolean; provider: string | null; classes: ClassOverview[] };

export type CoursesResult =
  | { deny: true; reason: string }
  | { deny: false; provider: string; courses: { externalClassId: string; name: string; section?: string }[] };

export type CourseLinkResult =
  | { deny: true; reason: string }
  | { deny: false; classId: string; provider: string; externalClassId: string };

export type ReleaseResult =
  | { deny: true; reason: string }
  | {
      deny: false;
      posted: { userId: string; score: number }[];
      skipped: { userId: string; reason: string }[];
      failed: { userId: string; reason: string }[];
      alreadyPosted: { userId: string }[];
      inFlight: { userId: string }[];
      lineItem: { externalLineItemId: string };
      maxScore: number;
    };

export interface AuditResult {
  tenantId: string;
  appId: string | null;
  since: string;
  lines: string[];
}

// ── Student home shapes (mirror server/student/home.ts) ──
export interface HomeAssignment {
  assignmentId: string;
  title: string;
  appSlug: string;
  appName: string;
  maxScore: number;
  myScore: number | null;
}
export interface HomeClass {
  classId: string;
  name: string;
  assignments: HomeAssignment[];
}
export type StudentHomeResult =
  | { deny: true; reason: string }
  | { deny: false; classes: HomeClass[] };

/** Mirror of server/db/roster-integrity.ts. The Teacher Console reads ok + counts;
 *  the District Console renders each finding, so the element shapes are typed here.
 *  Ids are surrogate uuids — DISPLAY-compacted (compactIds) at render, never trusted. */
export interface RosterMemberNotStudent { classId: string; userId: string; role: string; }
export interface RosterClassTaughtByStudent { classId: string; teacherUserId: string; }
export interface RosterStudentSuspectGrade { userId: string; gradeLevel: string | number }
export interface RosterStudentMissingGrade { userId: string; }
export interface RosterIntegrityReport {
  ok: boolean;
  membersNotStudent: RosterMemberNotStudent[];
  classesTaughtByStudents: RosterClassTaughtByStudent[];
  studentsWithSuspectGrade: RosterStudentSuspectGrade[];
  studentsMissingGrade: RosterStudentMissingGrade[];
}

// ── Self-serve LMS connect (mirrors server/connect) ──────────────────────────

/**
 * What the console shows about the teacher's own LMS connection. `source` is the
 * load-bearing field: 'own' means this teacher granted the permission themselves,
 * 'pilot_bridge' means they are borrowing the deployment's shared credential — which
 * works, but means grades leave under somebody else's Google account. The console says
 * which, because one chip reading "connected" for both would hide exactly the thing the
 * connection store exists to fix.
 */
export interface ConnectionStatus {
  provider: string;
  connected: boolean;
  source: 'own' | 'pilot_bridge' | 'none';
  account: string | null;
  connectedAt: string | null;
  /**
   * Google stopped accepting the credential this teacher last had, and nothing else is in
   * force: show "reconnect", never "connected". Optional because the shell and the server
   * deploy separately — an older server simply never sends it.
   */
  needsReconnect?: boolean;
  connectable: boolean;
  scopes: string[];
}

/**
 * A Classroom-backed route answered 409 {error:'reconnect_required'}: Google refused the
 * teacher's stored credential. Typed so the console can switch its connection card to
 * "reconnect" from ANY route that discovers it — not only from GET /teacher/connect.
 */
export class ReconnectRequiredError extends Error {
  constructor(readonly provider: string) {
    super('Google stopped accepting TIP’s access to your Google Classroom. Reconnect to keep sending grades.');
    this.name = 'ReconnectRequiredError';
  }
}

/** Throw ReconnectRequiredError for a 409 reconnect_required; otherwise do nothing. */
async function throwIfReconnect(r: Response): Promise<void> {
  if (r.status !== 409) return;
  const body = (await r.clone().json().catch(() => null)) as { error?: unknown; provider?: unknown } | null;
  if (body && body.error === 'reconnect_required') {
    throw new ReconnectRequiredError(typeof body.provider === 'string' ? body.provider : 'google_classroom');
  }
}

export interface DisconnectResult {
  disconnected: boolean;
  /** False ⇒ dead in TIP, but the teacher's Google account may still list TIP. */
  revokedAtGoogle: boolean;
}

// ── District-console shapes (mirror server/admin/overview.ts + admin/kill|restore) ──

export interface AdminApp {
  id: string;
  slug: string;
  name: string;
  enabled: boolean;
  manifestValid: boolean;
  requestedTier: 'standard' | 'high_assurance' | null;
  capabilities: string[];
  requestedFields: string[];
}
/**
 * "Did the clarifying interview help?" — see server/admin/clarify-effect.ts for what each
 * number is evidence of, and for why none of it is causal. The unit is a LINEAGE (one
 * teacher's one attempt at one activity), never a build row.
 */
export interface ClarifyCohortStats {
  cohort: 'one_shot' | 'clarified' | 'asked_unused';
  lineages: number;
  published: number;
  abandoned: number;
  publishedFirstShot: number;
  /** null = too few published lineages for an average to mean anything. */
  avgRefinesBeforePublish: number | null;
  firstRun: { clean: number; withErrors: number; blank: number; unreachable: number; never: number };
}
export interface ClarifyEffectResult {
  deny: false;
  cohorts: ClarifyCohortStats[];
  totalLineages: number;
  readings: string[];
}

/** The audit hash-chain verdict — the District Console's closing statement. */
export type AdminChain =
  | { ok: true; length: number; head: string }
  | { ok: false; brokenAt: number; reason: string };
export interface AdminOverviewResult {
  apps: AdminApp[];
  counts: { classes: number; students: number; teachers: number };
  chain: AdminChain;
}
/** POST /admin/kill result (the district-admin suspend path). `audit.ts` is the
 *  on-the-record timestamp the console echoes back in the row. */
export interface AdminKillResult {
  ok: boolean;
  source: string;
  quarantined: { tenantId: string; appId: string; scope: string };
  disabled: boolean;
  audit: { method: string; hash: string; ts: string };
}
export interface AdminRestoreResult {
  ok: boolean;
  enabled: boolean;
  audit: { hash: string; ts: string };
}

// ── Build-harness shapes (mirror server/harness/pipeline + POST /teacher/build) ──

/** One finding from a gate. `severity:'reject'` is what actually stopped a build. */
export interface BuildFinding {
  rule: string;
  severity: 'reject' | 'warn';
  message: string;
  line?: number;
}
/** One gate's verdict. `label`/`description`/`summary` are the SERVER's plain English —
 *  the console renders them verbatim and never invents a gate name or its order. */
export interface BuildStage {
  stage: string; // machine name (the audit record's stage id)
  label: string;
  description: string;
  verdict: 'pass' | 'fail' | 'skipped';
  summary: string;
  findings: BuildFinding[];
}
/** What the platform removed from the description before it left the district. */
export interface BuildRedaction {
  kind: string;
  action: 'redacted' | 'blocked' | 'advisory';
  message: string;
}
/** POST /teacher/build result (the server's BuildResponse). A `status:'failed'` build
 *  is a 200 — the gates refusing an app is a normal outcome, not a transport error. */
/** One clarifying question with the options a teacher taps. Server-generated. */
export interface ClarifyQuestion {
  id: string;
  ask: string;
  options: string[];
}

/** What the console sends back after the interview. The server composes; we only relay. */
export interface ClarifyAnswers {
  questions: ClarifyQuestion[];
  /** question id → the option string the teacher picked. Skipped questions are absent. */
  answers: Record<string, string>;
  /** The one optional free-text field in the flow. Redacted server-side like everything. */
  note?: string | undefined;
}

/**
 * One first-party starter app a teacher can pick instead of describing one (mirrors
 * `TemplateChoice` in server/harness/templates/provider.ts). NO SOURCE, by design — a
 * chooser needs enough to choose and nothing else.
 */
export interface TemplateChoice {
  slug: string;
  title: string;
  /** One line: what the activity is. */
  blurb: string;
  /** What kind of content the mechanic carries — the "will mine fit?" answer. */
  carries: string;
  /** Optional for compatibility with an older server's catalog. */
  compareTo?: string;
  /** The comparable function, rather than a full-product equivalence claim. */
  comparison?: string;
  /** sha256 of the pinned bytes. Display-only here; the SERVER decides what it ships. */
  sha256: string;
}

export interface BuildResult {
  materialDraftId?:string;
  materialsApproval?:{draftId:string;revision:number;mode:'list'|'gameplay'};
  kind?: 'assignment'|'resource';
  resourceAppId?: string | null;
  id: string;
  status: 'generating' | 'gating' | 'passed' | 'failed' | 'published' | 'interrupted';
  previewStatus?: TestRunStatus | null;
  prompt: string;
  title?: string;
  slug?: string;
  attempts: number;
  model: string;
  provider: string;
  capabilities: string[];
  source?: string;
  failureReason?: string;
  redactions: BuildRedaction[];
  stages: BuildStage[];
  /** Set when this build refines another. */
  parentBuildId?: string;
  /** Publishing this would REPLACE a live app rather than create one. */
  supersedes?: boolean;
  /** The tier it was built at — the school's when that is stricter than the choice made. */
  tier?: Tier;
}
/** POST /teacher/build/:id/test-run — permission to run the draft, plus the credential
 *  the sealed frame needs to fetch its unpublished source. */
export interface TestRunGrant {
  kind?: 'assignment'|'resource';
  artifactSha256?: string;
  buildId: string;
  tier: Tier;
  title: string;
  alreadyPublished: boolean;
  draftToken: string;
}
/** What the frame reported. Diagnostics — never authority (see migration 0010). */
export type TestRunStatus = 'ran_clean' | 'ran_with_errors' | 'blank' | 'unreachable';
/** POST /teacher/build/:id/publish result. Publishing creates the app AND the assignment
 *  that gives a score somewhere to land, so a class is required to publish at all. */
export interface PublishResult {
  /** Publication committed; optional library bookkeeping is still pending. */
  libraryPinStatus?: 'pending';
  kind?: 'assignment'|'resource';
  state?: ResourceView['state'];
  installationId?: string;
  artifactId?: string;
  revision?: number;
  appId: string;
  slug: string;
  name: string;
  assignmentId?: string;
}
export interface BuildListItem {
  kind?: 'assignment'|'resource';
  id: string;
  status: BuildResult['status'];
  title?: string;
  prompt: string;
  createdAt: string;
  parentBuildId?: string;
  previewStatus?: TestRunStatus | null;
}
export interface BuildListResult {
  builds: BuildListItem[];
  nextOffset?: number | null;
}

/**
 * A build or publish the server would not do: a 400 (`{error}`), or a prompt the
 * redactor BLOCKED before it reached the model (`{error, blocked:true}`). The message
 * is written for teachers — the console shows it verbatim and never rewrites it.
 */
export class BuildRefused extends Error {
  readonly blocked: boolean;
  /** The HTTP status, when the server answered. A 4xx is a deliberate refusal; a 5xx or a
   *  gateway timeout may hide a build that is still running. */
  readonly status: number | undefined;
  constructor(message: string, blocked: boolean, status?: number) {
    super(message);
    this.name = 'BuildRefused';
    this.blocked = blocked;
    this.status = status;
  }
}

/**
 * Whether a failed build POST may have left a build running on the server. Only a 4xx is a
 * deliberate refusal that created nothing; a lost connection, a 5xx or a gateway timeout
 * may not be. The shell keeps that press's request key in those cases, so pressing Build
 * again with the same inputs reattaches to the original build (migration 0025) instead of
 * spending the allowance and model calls twice.
 */
export function buildMayStillBeRunning(err: unknown): boolean {
  return !(err instanceof BuildRefused) || err.status === undefined || err.status >= 500;
}

/**
 * A 401 on an authenticated call means the session is over — it expired (12 hours), or it
 * was revoked (signed out in another tab). Before 2026-09-30 that surfaced as a raw
 * "/teacher/overview → HTTP 401" in the middle of the console, which tells a teacher
 * nothing. The bootstrap registers what to do instead (back to the sign-in screen, with a
 * sentence saying why); this module only reports that it happened.
 */
let sessionEnded: (() => void) | null = null;
export function onSessionEnded(fn: () => void): void { sessionEnded = fn; }
function throwIfSessionEnded(r: Response, path: string): void {
  if (r.status !== 401) return;
  sessionEnded?.();
  throw new Error(`${path} → your session has ended`);
}

export class ServerClient implements MaterialsClient {
  private pendingMaterialImport: {signature:string;requestKey:string}|undefined;
  constructor(private readonly base: string) {}

  private async materialRequest(path:string,body?:unknown):Promise<MaterialsDraftView> {
    const response=await fetch(this.base+path,{method:body===undefined?'GET':'POST',credentials:'include',
      headers:{'content-type':'application/json'},...(body===undefined?{}:{body:JSON.stringify(body)})});
    throwIfSessionEnded(response,path);
    const value=await response.json();
    if(!response.ok)throw Object.assign(new Error(typeof value?.error==='string'?value.error:'TIP could not finish this request. Your saved draft is unchanged.'),{status:response.status});
    return value as MaterialsDraftView;
  }
  async createMaterials(input:MaterialsInput,_tier:Tier):Promise<MaterialsDraftView>{
    // Remember only a digest and an opaque key, never a second copy of the document.
    // A lost response reattaches to the same paid request; changed material starts anew.
    const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(JSON.stringify(input)));
    const signature=Array.from(new Uint8Array(digest),x=>x.toString(16).padStart(2,'0')).join('');
    if(this.pendingMaterialImport?.signature!==signature)this.pendingMaterialImport={signature,requestKey:crypto.randomUUID()};
    const requestKey=this.pendingMaterialImport.requestKey;
    try{
      const result=await this.materialRequest('/teacher/materials',{input,requestKey});
      if(this.pendingMaterialImport?.requestKey===requestKey)this.pendingMaterialImport=undefined;
      return result;
    }catch(error){
      const status=(error as {status?:number})?.status;
      if(status&&status>=400&&status<500&&status!==429&&this.pendingMaterialImport?.requestKey===requestKey)this.pendingMaterialImport=undefined;
      throw error;
    }
  }
  getMaterials(id:string){return this.materialRequest('/teacher/materials/'+encodeURIComponent(id));}
  updateMaterials(id:string,expectedRevision:number,content:FlipCardContent){return this.materialRequest('/teacher/materials/'+encodeURIComponent(id)+'/edit',{expectedRevision,content});}
  compileMaterials(id:string,expectedRevision:number){return this.materialRequest('/teacher/materials/'+encodeURIComponent(id)+'/compile',{expectedRevision});}
  approveMaterials(id:string,expectedRevision:number,mode:MaterialsReviewMode){return this.materialRequest('/teacher/materials/'+encodeURIComponent(id)+'/approve',{expectedRevision,mode});}
  selectMaterialsUnit(id:string,expectedRevision:number,unitId:string){return this.materialRequest('/teacher/materials/'+encodeURIComponent(id)+'/unit',{expectedRevision,unitId});}

  /** The public privacy page tip-server serves (text approved by Steve, 2026-09-30). */
  privacyUrl(): string { return `${this.base}/privacy`; }

  /**
   * Sign out: the server REVOKES the session row (so every copy of the token stops
   * working) and clears the cookie. Throws if TIP did not answer — the caller must then
   * say the teacher is still signed in, never pretend otherwise.
   */
  async logout(): Promise<{ revoked: boolean }> {
    const r = await fetch(this.base + '/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!r.ok) throw new Error(`/auth/logout → HTTP ${r.status}`);
    return (await r.json()) as { revoked: boolean };
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const r = await fetch(this.base + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    throwIfSessionEnded(r, path);
    await throwIfReconnect(r);
    if (!r.ok) throw new Error(`${path} → HTTP ${r.status}`);
    return (await r.json()) as T;
  }

  /**
   * SSO session probe. GET /auth/session with the session cookie (credentials:'include');
   * the server resolves tenant + user from the cookie. A 401 (no / expired / unverified
   * session) resolves to null so the bootstrap can fail-closed into the login gate — it
   * NEVER throws for the unauthenticated case. Other non-2xx still throw (real error).
   *
   * IT RETRIES A COLD SERVER, and the distinction it draws is the whole point.
   *
   * The server runs on Cloud Run with no minimum instance, so it is SHUT DOWN when idle
   * and the first request after a gap has to wait for a container to boot (which also
   * transpiles TypeScript at start-up). Observed live 2026-07-25: that first request
   * failed outright, and because this probe is the very first thing `mountShell` does,
   * the teacher got "ERROR at mount — fail-closed" on a server that was merely waking up.
   * That is a terrible thing to have on screen in front of a district, and it is a lie —
   * nothing had failed.
   *
   * ⚠ WHAT IS AND IS NOT RETRIED — do not blur these:
   *   • 401 → returns null IMMEDIATELY, never retried. It is a definitive answer ("you
   *     are not signed in"), and retrying it would delay the login gate for every
   *     logged-out visitor. The fail-closed path must stay instant.
   *   • A network-level failure, or 502/503/504 (Cloud Run's "no instance yet") → the
   *     server has not answered at all. Retry with backoff.
   *   • Any other non-2xx → throw. A 403 or a 500 is a real answer and must surface.
   *
   * Bounded at roughly 30 seconds total, then it throws like before — a server that is
   * genuinely down must still fail, just not instantly and not with the wrong story.
   */
  async session(opts: { onWaking?: ((attempt: number) => void) | undefined } = {}): Promise<SessionInfo | null> {
    // Backoff schedule; the sum is the total patience (~29.5s).
    const DELAYS = [500, 1_000, 2_000, 4_000, 6_000, 8_000, 8_000];
    for (let attempt = 0; ; attempt++) {
      const last = attempt >= DELAYS.length;
      let r: Response;
      try {
        r = await fetch(this.base + '/auth/session', {
          method: 'GET',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
        });
      } catch (e) {
        // No response at all — DNS, connection refused, the instance still booting.
        if (last) throw e;
        opts.onWaking?.(attempt + 1);
        await new Promise((res) => setTimeout(res, DELAYS[attempt]));
        continue;
      }
      if (r.status === 401) return null; // definitive; never retried
      if (r.status === 502 || r.status === 503 || r.status === 504) {
        if (last) throw new Error(`/auth/session → HTTP ${r.status}`);
        opts.onWaking?.(attempt + 1);
        await new Promise((res) => setTimeout(res, DELAYS[attempt]));
        continue;
      }
      if (!r.ok) throw new Error(`/auth/session → HTTP ${r.status}`);
      return (await r.json()) as SessionInfo;
    }
  }

  /** Mount-time effective-grant fetch. The Shell enforces the tier this returns.
   *  `assignmentId` (from the Today home launch) binds the mount to a specific
   *  assignment; the server verifies it belongs to the app AND the viewer. */
  gateMount(appId: string, assignmentId?: string): Promise<GateMountResult> {
    return this.post<GateMountResult>(
      '/gate/mount',
      assignmentId ? { app_id: appId, assignment_id: assignmentId } : { app_id: appId },
    );
  }

  /** Registrar-backed tag→name resolution. Used to build the standard tip:init and
   *  to paint high_assurance parent-DOM slots. Names NEVER cross into the frame from
   *  here unless the tier legitimately allows it (standard init only). */
  resolveIdentity(tags: string[]): Promise<ResolveResult> {
    return this.post<ResolveResult>('/identity/resolve', { tags });
  }

  /** Scope-ref leaderboard (the frame's renderLeaderboard target). Returns the
   *  broker-authoritative ranked name+score rows the Shell paints in the PARENT DOM.
   *  Names in the result are trusted-chrome only — the Shell must never post them into
   *  the frame. A deny (fail-closed) resolves to `{deny:true}` (still a 200). */
  resolveLeaderboard(appId: string, scope: { classId: string; assignmentId: string }): Promise<LeaderboardResult> {
    return this.post<LeaderboardResult>('/leaderboard/resolve', {
      app_id: appId, classId: scope.classId, assignmentId: scope.assignmentId,
    });
  }

  /** Door-1 broker proxy. Failures resolve to an error result (never throw across the Bridge). */
  async broker(
    reqId: string,
    method: BrokerMethod,
    args: unknown,
    appId: string,
  ): Promise<BrokerResult> {
    try {
      // camelCase appId to match the server envelope (server reads body.appId).
      return await this.post<BrokerResult>('/broker', { reqId, method, args, appId });
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  }

  studentLibraryFeedback(mountId: string): Promise<StudentLibraryFeedback> {
    return this.get(`/student/library-feedback/${encodeURIComponent(mountId)}`);
  }

  rateLibraryRelease(mountId: string, rating: StudentLibraryRating): Promise<{ok: true; rating: StudentLibraryRating}> {
    return this.postTeacherFacing(`/student/library-feedback/${encodeURIComponent(mountId)}`, rating);
  }

  // ── Teacher console (trusted chrome only — none of this is frame-reachable) ──

  private async get<T>(path: string): Promise<T> {
    const r = await fetch(this.base + path, { method: 'GET', credentials: 'include' });
    throwIfSessionEnded(r, path);
    await throwIfReconnect(r);
    if (!r.ok) throw new Error(`${path} → HTTP ${r.status}`);
    return (await r.json()) as T;
  }

  /** Console rollup: my classes, link state, per-assignment score/outbox status. */
  teacherOverview(): Promise<OverviewResult> {
    return this.get<OverviewResult>('/teacher/overview');
  }

  /** Provider courses available to link (course id + title — provider metadata). */
  teacherCourses(): Promise<CoursesResult> {
    return this.get<CoursesResult>('/teacher/courses');
  }

  /** Attest a class↔course link (the stored §16.3 egress destination). */
  linkCourse(classId: string, externalClassId: string): Promise<CourseLinkResult> {
    return this.post<CourseLinkResult>('/teacher/link-course', { classId, externalClassId });
  }

  /** Release an assignment's REAL scores to the linked gradebook (draft grades). */
  releaseGrades(assignmentId: string): Promise<ReleaseResult> {
    return this.post<ReleaseResult>('/teacher/release', { assignmentId });
  }

  /** Plain-English audit lines. No appId ⇒ the tenant-wide view (includes
   *  tenant-level events like course-link attestations). */
  auditLines(appId?: string): Promise<AuditResult> {
    return this.get<AuditResult>(appId ? `/audit?appId=${encodeURIComponent(appId)}` : '/audit');
  }

  /** District Console: every school and every class's school (admin only). */
  adminSchools(): Promise<SchoolsView> {
    return this.get<SchoolsView>('/admin/schools');
  }

  /** Create (no id) or update a school. A refusal comes back as `{ok:false, reason}`. */
  saveSchool(input: { id?: string; name: string; mascot: string; primary: string; accent: string }): Promise<SchoolSaveResult> {
    return this.post<SchoolSaveResult>('/admin/schools', input);
  }

  /** Put a class in a school, or take it out (`null`). */
  setClassSchool(classId: string, schoolId: string | null): Promise<{ ok: boolean; reason?: string }> {
    return this.post<{ ok: boolean; reason?: string }>('/admin/classes/school', { classId, schoolId });
  }

  /** Pilot roster-integrity report (surrogates only; violations break trust assumptions). */
  rosterIntegrity(): Promise<RosterIntegrityReport> {
    return this.get<RosterIntegrityReport>('/admin/roster/integrity');
  }

  // ── Self-serve LMS connect ──

  /** The teacher's own LMS connection state (never a credential). */
  connectionStatus(): Promise<ConnectionStatus> {
    return this.get<ConnectionStatus>('/teacher/connect');
  }

  /**
   * Where the "Connect Google Classroom" control navigates. A TOP-LEVEL navigation, NOT
   * a fetch: the flow ends at Google's consent screen, which cannot be rendered inside
   * an XHR — and must not be rendered inside a frame either. The server sets the signed
   * flow cookie on the 302 out.
   */
  connectStartUrl(): string {
    return `${this.base}/teacher/connect/google/start`;
  }

  /** Withdraw TIP's standing permission: revoked at Google (best effort) and locally. */
  disconnectProvider(): Promise<DisconnectResult> {
    return this.post<DisconnectResult>('/teacher/connect/google/revoke', {});
  }

  // ── Build harness (describe → build → gates → publish) ──

  /**
   * POST whose refusal body is load-bearing: the build endpoints answer a 4xx with
   * `{error}` written FOR TEACHERS (plus `blocked:true` when the redactor stopped a
   * prompt before it left the district). `post()` keeps only the status code, so these
   * calls get their own reader and raise the server's sentence as a BuildRefused.
   *
   * DELIBERATELY un-timed: no AbortController, no signal. A build runs for minutes and
   * a client timeout would abandon work the server is still doing and still recording.
   */
  private async postTeacherFacing<T>(path: string, body: unknown): Promise<T> {
    const r = await fetch(this.base + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    throwIfSessionEnded(r, path);
    if (!r.ok) {
      const refusal = (await r.json().catch(() => null)) as { error?: string; blocked?: boolean } | null;
      throw new BuildRefused(refusal?.error ?? `${path} → HTTP ${r.status}`, refusal?.blocked === true, r.status);
    }
    return (await r.json()) as T;
  }

  /**
   * Ask TIP what is MISSING from a description before building anything.
   *
   * Returns questions with pickable options, or an EMPTY list when the description is
   * already clear (or the model misbehaved — the server fails soft here on purpose).
   * An empty list is not an error: the caller should just build. Writes no app, runs no
   * gate; only a BLOCKED prompt refuses, and that is the redactor, not the interview.
   */
  clarifyBuild(description: string): Promise<{ questions: ClarifyQuestion[] }> {
    return this.postTeacherFacing<{ questions: ClarifyQuestion[] }>(
      '/teacher/build/clarify',
      { description },
    );
  }

  libraryCatalog(query: LibraryQuery): Promise<LibraryCatalogResult> {
    return this.get(`/teacher/library?${libraryQueryString(query)}`);
  }
  async libraryRelease(id: string, window: 'all' | '30d' = 'all'): Promise<LibraryDetailResult> {
    const result = await this.get<LibraryDetailResult>(`/teacher/library/${encodeURIComponent(id)}?window=${window}`);
    // The metric endpoint is exact-release scoped. A read failure cannot manufacture a zero.
    try {
      const response = await this.get<{metrics: LibraryReleaseMetrics[]}>(`/teacher/library-feedback/metrics?releaseIds=${encodeURIComponent(result.release.id)}&window=${window}`);
      const metrics = response.metrics.find(m => m.releaseId === result.release.id && m.window === window);
      const release = {...result.release};
      delete release.metrics;
      return {...result, release: metrics ? {...release, metrics} : release};
    } catch {
      const release = {...result.release}; delete release.metrics;
      return {...result, release};
    }
  }
  shareLibraryBuild(input: LibraryShareInput): Promise<{release: RatedLibraryRelease}> {
    return this.postTeacherFacing('/teacher/library/share', input);
  }
  adoptLibraryRelease(id: string, mode: 'adopt' | 'remix'): Promise<{buildId: string; baseReleaseId: string; mode: 'adopt' | 'remix'}> {
    return this.postTeacherFacing(`/teacher/library/${encodeURIComponent(id)}/adopt`, {mode});
  }
  withdrawLibraryRelease(id: string): Promise<{withdrawn: true}> {
    return this.postTeacherFacing(`/teacher/library/${encodeURIComponent(id)}/withdraw`, {});
  }

  resources(): Promise<{enabled:boolean;resources:ResourceView[]}> { return this.get('/resources'); }
  mountResource(id: string): Promise<GateMountResult> { return this.post(`/resources/${encodeURIComponent(id)}/mount`,{}); }
  setResourceState(id: string, state: ResourceView['state'], revision: number): Promise<{ok:true}> {
    return this.postTeacherFacing(`/teacher/resources/${encodeURIComponent(id)}/state`,{state,revision});
  }

  /** Whether the school has fixed the tier for every app (pilot schools: name-blind). */
  teacherBuildSettings(): Promise<{ districtTier: Tier; resourcesEnabled?: boolean }> {
    return this.get<{ districtTier: Tier }>('/teacher/build/settings');
  }

  /** The first-party starter apps a teacher can pick instead of describing one. Cheap and
   *  DB-free server-side; a failure here is non-blocking (the describe box still works). */
  teacherTemplates(kind: 'assignment'|'resource' = 'assignment'): Promise<{ templates: TemplateChoice[] }> {
    return this.get<{ templates: TemplateChoice[] }>(`/teacher/templates?kind=${kind}`);
  }

  /** Build an app from a plain-language description. SYNCHRONOUS and SLOW — the model
   *  writes it, the ship pipeline gates it, and a repair loop may retry, so this can run
   *  for minutes (see the timeout note on postTeacherFacing).
   *
   *  `clarify` carries the answers from the interview above. The SERVER folds them into
   *  the description (upstream of redaction) — this client never composes the prompt. */
  buildApp(description: string, tier?: Tier, clarify?: ClarifyAnswers, kind: 'assignment'|'resource' = 'assignment', requestKey?: string): Promise<BuildResult> {
    return this.postTeacherFacing<BuildResult>('/teacher/build', {
      description, kind,
      ...(requestKey ? { requestKey } : {}),
      ...(tier ? { tier } : {}),
      ...(clarify ? { questions: clarify.questions, answers: clarify.answers, note: clarify.note } : {}),
    });
  }

  /**
   * Build a first-party starter app the teacher PICKED, by slug.
   *
   * ⚠ NO DESCRIPTION IS SENT, and that is the contract, not an omission. The bytes are
   * pinned, so nothing typed could change what comes out; sending a sentence anyway would
   * put a cause on the district's record that this build did not have. The teacher makes
   * it theirs afterwards, through `refineBuild` — a turn that genuinely does drive the
   * model, and is recorded because it does.
   *
   * Fast, unlike every other build: no provider call, so this returns in about the time
   * the six gates take rather than in minutes.
   */
  buildFromTemplate(slug: string, tier?: Tier, kind: 'assignment'|'resource' = 'assignment'): Promise<BuildResult> {
    return this.postTeacherFacing<BuildResult>('/teacher/build', {
      templateSlug: slug, kind,
      ...(tier ? { tier } : {}),
    });
  }

  /** Publish a PASSED build into a class the teacher teaches: it becomes a real app AND
   *  the assignment its scores land on, so `classId` is REQUIRED. The server re-checks
   *  the gate verdicts and the teacher's claim on the class; both refusals arrive as
   *  teacher-facing prose, never as a status code. */
  publishBuild(buildId: string, classId: string, expectedRevision?: number): Promise<PublishResult> {
    return this.postTeacherFacing<PublishResult>(
      `/teacher/build/${encodeURIComponent(buildId)}/publish`,
      { classId, ...(expectedRevision === undefined ? {} : {expectedRevision}) },
    );
  }

  /** This teacher's own saved versions, newest first; source is fetched only on open. */
  teacherBuilds(offset = 0, kind?: 'assignment'|'resource'): Promise<BuildListResult> {
    return this.get<BuildListResult>(`/teacher/builds?offset=${offset}${kind ? `&kind=${kind}` : ''}`);
  }

  teacherBuild(buildId: string): Promise<BuildResult> {
    return this.get<BuildResult>(`/teacher/build/${encodeURIComponent(buildId)}`);
  }

  /** Ask permission to test-run a draft. The server proves authorship once and hands back
   *  a short-lived token bound to (tenant, build); the sealed frame redeems it for the
   *  unpublished source. Refusals arrive as teacher-facing prose. */
  testRunGrant(buildId: string): Promise<TestRunGrant> {
    return this.postTeacherFacing<TestRunGrant>(
      `/teacher/build/${encodeURIComponent(buildId)}/test-run`,
      {},
    );
  }

  /** Report what the frame did. Recorded as evidence that a human ran these exact bytes;
   *  publishing is refused without it. It can relax no safety gate. */
  reportTestRun(buildId: string, status: TestRunStatus, detail: string, artifactSha256?: string): Promise<{ ok: true }> {
    return this.postTeacherFacing<{ ok: true }>(
      `/teacher/build/${encodeURIComponent(buildId)}/test-run/result`,
      { status, detail, ...(artifactSha256 ? {artifactSha256} : {}) },
    );
  }

  /** "Make it harder." A refinement is a NEW build whose parent is named; it runs the
   *  same six gates, and the live app keeps serving the old version until this one is
   *  published in its place. Slow, like a first build. */
  refineBuild(buildId: string, instruction: string, requestKey?: string): Promise<BuildResult> {
    return this.postTeacherFacing<BuildResult>(
      `/teacher/build/${encodeURIComponent(buildId)}/refine`,
      { instruction, ...(requestKey ? { requestKey } : {}) },
    );
  }

  async sendPilotFeedback(input: PilotFeedbackInput): Promise<{id:string}> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const r = await fetch(this.base + '/teacher/feedback', {method:'POST',credentials:'include',headers:{'content-type':'application/json'},body:JSON.stringify(input),signal:controller.signal});
      // Keep the note on-screen even on expiry; no automatic login redirect here.
      if (r.status === 401) throw new Error('Your session ended. Copy your note, sign in again, then send it.');
      const body = await r.json().catch(() => null);
      if (!r.ok) throw new Error(typeof body?.error === 'string' ? body.error : 'Feedback could not be sent. Your note is still here; please try again.');
      if (!body || typeof body.id !== 'string' || !/^[0-9a-f-]{36}$/i.test(body.id)) throw new Error('The server did not confirm your report. Please try again.');
      return {id:body.id};
    } catch(e) {
      if(e instanceof TypeError || e instanceof DOMException) throw new Error('The connection was interrupted. Your note is still here; please try again.');
      throw e;
    } finally { clearTimeout(timer); }
  }
  pilotFeedbackReports(offset=0): Promise<{reports:PilotFeedbackReport[];hasMore:boolean}> { return this.get('/admin/pilot-feedback?offset='+offset); }
  setPilotFeedbackStatus(id:string,status:FeedbackStatus): Promise<{ok:true}> { return this.post('/admin/pilot-feedback/'+encodeURIComponent(id)+'/status',{status}); }

  // ── District console (admin seat; trusted chrome; none of this is frame-reachable) ──

  /** District overview: apps in force + the exact grant each holds, tenant counts, and
   *  the audit hash-chain verdict. Admin only (403 else; 501 when no DB) — both throw. */
  adminOverview(): Promise<AdminOverviewResult> {
    return this.get<AdminOverviewResult>('/admin/overview');
  }

  /** "Did the clarifying interview help?" — refine turns, abandonment, and the first
   *  test-run verdict, split by whether the teacher used the interview. Admin only.
   *  Counts only: this carries no prompt text, no names, no surrogate ids. Advisory —
   *  the console must never let a failure here block the page. */
  clarifyEffect(): Promise<ClarifyEffectResult> {
    return this.get<ClarifyEffectResult>('/admin/clarify-effect');
  }

  /** District-admin suspend (POST /admin/kill, the tenant-wide kill switch). Distinct
   *  from the tripwire `adminKill` above by design: this returns the FULL result (the
   *  audit ts for the on-the-record line) and THROWS on non-2xx, so the console renders
   *  the failure flat in the row ("Could not: …") rather than a silent boolean. */
  adminSuspend(appId: string, reason: string): Promise<AdminKillResult> {
    return this.post<AdminKillResult>('/admin/kill', { appId, reason });
  }

  /** Lift a district suspend (POST /admin/restore). Admin only; 404 unknown_app throws. */
  adminRestore(appId: string): Promise<AdminRestoreResult> {
    return this.post<AdminRestoreResult>('/admin/restore', { appId });
  }

  /** Student "Today" home: MY classes + launchable assignments + MY score only. */
  studentHome(): Promise<StudentHomeResult> {
    return this.get<StudentHomeResult>('/student/home');
  }

  /**
   * Quarantine (tripwire enforcement). ONE authoritative call: the server disables the
   * app tenant-wide AND writes the hash-chained egress_tripwire audit event atomically.
   * A `source='egress_tripwire'` kill is honored regardless of session role (it is a
   * fail-closed safety action from the trusted parent). Resolves true iff confirmed.
   */
  async adminKill(
    appId: string,
    reason: string,
    source: 'admin' | 'egress_tripwire' = 'egress_tripwire',
    detectedAt?: string,
  ): Promise<boolean> {
    try {
      await this.post('/admin/kill', { appId, reason, source, ...(detectedAt ? { detectedAt } : {}) });
      return true;
    } catch {
      return false;
    }
  }
}
